import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  getPlatformOrgs,
  updateOrgKnowledgeBaseTrustCenterEnabled,
  getTrustCenterOrgs,
  revokeOrgTrustCenterPublicLink,
  updateOrgTrustCenterPortalEnabled,
  resetOrgTrustCenterHostname,
  getOrgKnowledgeBaseDomainSources,
  updateOrgKnowledgeBaseDomainSources,
} from '../../Api/Platform';
import { GetPolicyCenterData } from '../../Api/PolicyCenter';
import { GetControlData } from '../../Api/Control';

const TAB_KEYS = {
  KNOWLEDGE_BASE: 'knowledge-base',
  SHARING_LINKS: 'sharing-links',
  DOMAIN_SOURCES: 'domain-sources',
};

const tabOrder = [TAB_KEYS.KNOWLEDGE_BASE, TAB_KEYS.SHARING_LINKS, TAB_KEYS.DOMAIN_SOURCES];

const tabLabels = {
  [TAB_KEYS.KNOWLEDGE_BASE]: 'Knowledge Base Access',
  [TAB_KEYS.SHARING_LINKS]: 'Sharing & Custom Links',
  [TAB_KEYS.DOMAIN_SOURCES]: 'Knowledge Base Domain Sources',
};

// The 6 Knowledge Base card_keys with no backing modal table (see
// iam_service/pkg/knowledge_base/domain_sources.go DomainSourceCardKeys) —
// labels match iam_service/pkg/knowledge_base/knowledge_base.go's Catalog
// exactly, so a Zavior Admin sees the same names the org sees.
const DOMAIN_SOURCE_CARD_KEYS = [
  { key: 'encryption', label: 'Encryption' },
  { key: 'logging-monitoring', label: 'Logging & Monitoring' },
  { key: 'incident-response', label: 'Incident Response' },
  { key: 'business-continuity', label: 'Business Continuity & DR' },
  { key: 'access-control', label: 'Access Control & Identity' },
  { key: 'personal-data-practices', label: 'Personal data practices' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

// ============================================================================
// TAB 1 — KNOWLEDGE BASE ACCESS
// ============================================================================
// Mirrors EmailSetupPage.jsx's "Organization Email Settings" tab, but lives
// in Trinity (Zavior-Admin only, via RequireSuperAdmin) rather than the
// customer-facing admin area, since this gate controls whether an org's own
// Trust Center admin can ever reveal Knowledge Base cards publicly.

const KnowledgeBaseAccessTab = () => {
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [savingId, setSavingId] = useState(null);

  const fetchOrgs = useCallback(() => {
    setLoading(true);
    getPlatformOrgs({ limit: rowsPerPage, offset: page * rowsPerPage })
      .then((res) => {
        setOrgs(res?.data?.data || []);
        setTotal(res?.data?.total || 0);
      })
      .catch((err) => {
        console.error('Failed to fetch organizations:', err);
        toast.error('Failed to load organizations');
        setOrgs([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const handleToggle = async (org) => {
    const next = !org.is_knowledge_base_trust_center_enabled;
    setSavingId(org.id);
    // Optimistic update so the switch feels instant.
    setOrgs((prev) => prev.map((o) => (
      o.id === org.id ? { ...o, is_knowledge_base_trust_center_enabled: next } : o
    )));
    try {
      await updateOrgKnowledgeBaseTrustCenterEnabled(org.id, next);
      toast.success(`Knowledge Base in Trust Center ${next ? 'enabled' : 'disabled'} for ${org.organization_name}`);
      fetchOrgs();
    } catch (err) {
      console.error('Failed to update Knowledge Base Trust Center gate:', err);
      toast.error('Failed to update setting');
      // Roll back the optimistic update.
      setOrgs((prev) => prev.map((o) => (
        o.id === org.id ? { ...o, is_knowledge_base_trust_center_enabled: !next } : o
      )));
    } finally {
      setSavingId(null);
    }
  };

  const filteredOrgs = useMemo(() => {
    if (!search.trim()) return orgs;
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => o.organization_name?.toLowerCase().includes(q));
  }, [orgs, search]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Controls whether an organization's own Trust Center admin can ever reveal
        Knowledge Base cards under the "Organization Details" section. Per-card
        visibility (hidden / portal / public) is still curated by the org itself —
        this switch only gates whether the section can be built at all.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="Search organizations"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organization Name</TableCell>
              <TableCell>Knowledge Base in Trust Center Enabled</TableCell>
              <TableCell>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading…' : 'No matching organizations'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredOrgs.map((org) => (
              <TableRow key={org.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {org.organization_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={!!org.is_knowledge_base_trust_center_enabled}
                    onChange={() => handleToggle(org)}
                    disabled={savingId === org.id}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(org.updated_at)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

// ============================================================================
// TAB 2 — SHARING & CUSTOM LINKS
// ============================================================================

const PublicLinkCell = ({ row, onRevoke, busy }) => {
  if (!row.is_public_enabled) {
    return <Chip size="small" label="Off" variant="outlined" />;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Chip size="small" color="success" label="Active" />
      <Typography variant="caption" color="text.secondary">
        since {formatDateTime(row.share_token_created_at)}
      </Typography>
      <Button size="small" color="error" variant="outlined" disabled={busy} onClick={() => onRevoke(row)}>
        Revoke
      </Button>
    </Box>
  );
};

const HostnameCell = ({ row, onReset, busy }) => {
  if (!row.public_hostname) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  const verified = !row.hostname_verify_token;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
        {row.public_hostname}
      </Typography>
      <Chip
        size="small"
        label={verified ? 'Verified' : 'Pending'}
        color={verified ? 'success' : 'warning'}
      />
      <Button size="small" color="error" variant="outlined" disabled={busy} onClick={() => onReset(row)}>
        Reset
      </Button>
    </Box>
  );
};

const SharingLinksTab = () => {
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [busyId, setBusyId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'revoke' | 'reset', row }

  const fetchOrgs = useCallback(() => {
    setLoading(true);
    getTrustCenterOrgs({ limit: rowsPerPage, offset: page * rowsPerPage, q: search.trim() || undefined })
      .then((res) => {
        setOrgs(res?.data?.data || []);
        setTotal(res?.data?.total || 0);
      })
      .catch((err) => {
        console.error('Failed to fetch Trust Center orgs:', err);
        toast.error('Failed to load Trust Center sharing status');
        setOrgs([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const handleTogglePortal = async (row) => {
    const next = !row.is_portal_enabled;
    setBusyId(row.id);
    setOrgs((prev) => prev.map((o) => (o.id === row.id ? { ...o, is_portal_enabled: next } : o)));
    try {
      await updateOrgTrustCenterPortalEnabled(row.id, next);
      toast.success(`Guest Portal ${next ? 'enabled' : 'disabled'} for ${row.organization_name}`);
    } catch (err) {
      console.error('Failed to update Guest Portal:', err);
      toast.error('Failed to update Guest Portal');
      setOrgs((prev) => prev.map((o) => (o.id === row.id ? { ...o, is_portal_enabled: !next } : o)));
    } finally {
      setBusyId(null);
    }
  };

  const requestRevoke = (row) => setConfirmAction({ type: 'revoke', row });
  const requestReset = (row) => setConfirmAction({ type: 'reset', row });

  const closeConfirm = () => setConfirmAction(null);

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, row } = confirmAction;
    setBusyId(row.id);
    try {
      if (type === 'revoke') {
        await revokeOrgTrustCenterPublicLink(row.id);
        toast.success(`Public link revoked for ${row.organization_name}`);
      } else if (type === 'reset') {
        await resetOrgTrustCenterHostname(row.id);
        toast.success(`Custom hostname reset for ${row.organization_name}`);
      }
      fetchOrgs();
    } catch (err) {
      console.error(`Failed to ${type} for org ${row.id}:`, err);
      toast.error(`Failed to ${type === 'revoke' ? 'revoke public link' : 'reset hostname'}`);
    } finally {
      setBusyId(null);
      closeConfirm();
    }
  };

  const filteredOrgs = useMemo(() => orgs, [orgs]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tracks every organization's Trust Center sharing setup — anonymous public
        link, authenticated Guest Portal, and custom hostname/CNAME — and lets a
        Zavior Admin revoke a public link, toggle the Guest Portal, or reset a
        misconfigured custom hostname for any organization.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="Search organizations"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organization Name</TableCell>
              <TableCell>Public Link</TableCell>
              <TableCell>Guest Portal</TableCell>
              <TableCell>Custom Hostname</TableCell>
              <TableCell>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading…' : 'No matching organizations'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredOrgs.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {row.organization_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <PublicLinkCell row={row} onRevoke={requestRevoke} busy={busyId === row.id} />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={!!row.is_portal_enabled}
                    onChange={() => handleTogglePortal(row)}
                    disabled={busyId === row.id}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <HostnameCell row={row} onReset={requestReset} busy={busyId === row.id} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(row.updated_at)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      <Dialog open={!!confirmAction} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmAction?.type === 'revoke' ? 'Revoke public link?' : 'Reset custom hostname?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction?.type === 'revoke' ? (
              <>
                This immediately breaks <strong>{confirmAction?.row?.organization_name}</strong>'s
                existing public Trust Center link. Anyone with the old link will get a 404. The org
                can generate a new link at any time from their own Trust Center settings.
              </>
            ) : (
              <>
                This clears <strong>{confirmAction?.row?.organization_name}</strong>'s custom hostname
                (<code>{confirmAction?.row?.public_hostname}</code>) and its verification token. Their
                Trust Center will fall back to the default zavior.app link until they configure a new one.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button color="error" variant="contained" onClick={runConfirmedAction} disabled={busyId === confirmAction?.row?.id}>
            {confirmAction?.type === 'revoke' ? 'Revoke link' : 'Reset hostname'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================================
// TAB 3 — KNOWLEDGE BASE DOMAIN SOURCES
// ============================================================================
// Reuses the same org search/table shape as KnowledgeBaseAccessTab (Tab 1)
// for org selection; clicking "Configure" opens DomainSourcesDialog, a
// per-org picker for the 6 narrative-only card_keys (Part 9 of the IP Trust
// Center Sections + Gating plan).

const DomainSourcesDialog = ({ org, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [controls, setControls] = useState([]);
  const [selections, setSelections] = useState({}); // card_key -> [{type,id,name}]
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      // Unfiltered by status (no 3rd/4th arg) so a Zavior Admin can pick
      // drafts too, not just Approved policies — unlike the org-facing
      // "Documented Policies" drilldown, which only counts Approved.
      GetPolicyCenterData(org.id, '', '', '').catch(() => ({ data: [] })),
      GetControlData(null, org.id).catch(() => ({ data: [] })),
      getOrgKnowledgeBaseDomainSources(org.id).catch(() => ({ data: { data: {} } })),
    ]).then(([policyRes, controlRes, sourcesRes]) => {
      if (cancelled) return;
      setPolicies((policyRes?.data || []).filter(Boolean));
      setControls((controlRes?.data || []).filter(Boolean));
      const byCardKey = sourcesRes?.data?.data || {};
      const next = {};
      DOMAIN_SOURCE_CARD_KEYS.forEach(({ key }) => { next[key] = byCardKey[key] || []; });
      setSelections(next);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [org.id]);

  // One combined option list (policies + controls) so each card_key's
  // Autocomplete can mix sources from either — grouped by type in the
  // dropdown/renderTags so it still reads as two distinct pools, mirroring
  // MixedRecordCurationDialog's own "stacked Policies/Controls" framing.
  const options = useMemo(() => ([
    ...policies.map((p) => ({ type: 'policy', id: p.id, name: p.name || 'Unnamed policy' })),
    ...controls.map((c) => ({ type: 'control', id: c.id, name: c.name || 'Unnamed control' })),
  ]), [policies, controls]);

  const resolveValue = (cardKey) => (selections[cardKey] || []).map(
    (v) => options.find((o) => o.type === v.type && o.id === v.id) || v,
  );

  const handleSave = async (cardKey) => {
    setSavingKey(cardKey);
    try {
      const sources = (selections[cardKey] || []).map((v) => ({ type: v.type, id: v.id }));
      const res = await updateOrgKnowledgeBaseDomainSources(org.id, cardKey, sources);
      setSelections((prev) => ({ ...prev, [cardKey]: res?.data?.data || [] }));
      const label = DOMAIN_SOURCE_CARD_KEYS.find((c) => c.key === cardKey)?.label || cardKey;
      toast.success(`Saved sources for "${label}"`);
    } catch (err) {
      console.error(`Failed to save domain sources for ${cardKey}:`, err);
      toast.error('Failed to save sources');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Knowledge Base Domain Sources — {org.organization_name}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          These 6 Knowledge Base cards have no source modal of their own — pick
          which of this organization's existing Policies and/or Controls should
          ground each one's AI-drafted paragraph. Each row saves independently.
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={3}>
            {DOMAIN_SOURCE_CARD_KEYS.map(({ key, label }) => (
              <Box key={key}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={options}
                  value={resolveValue(key)}
                  onChange={(_, value) => setSelections((prev) => ({ ...prev, [key]: value }))}
                  groupBy={(option) => (option.type === 'policy' ? 'Policies' : 'Controls')}
                  getOptionLabel={(option) => option.name || 'Unnamed'}
                  isOptionEqualToValue={(option, value) => option.type === value.type && option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} placeholder={options.length === 0 ? 'No policies or controls found' : 'Select policies or controls'} />
                  )}
                  renderTags={(value, getTagProps) => value.map((option, index) => (
                    <Chip
                      size="small"
                      key={`${option.type}-${option.id}`}
                      label={option.name}
                      color={option.type === 'control' ? 'primary' : 'default'}
                      variant={option.type === 'control' ? 'filled' : 'outlined'}
                      {...getTagProps({ index })}
                    />
                  ))}
                  noOptionsText="No policies or controls found"
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button size="small" variant="contained" disabled={savingKey === key} onClick={() => handleSave(key)}>
                    {savingKey === key ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const DomainSourcesTab = () => {
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editingOrg, setEditingOrg] = useState(null);

  const fetchOrgs = useCallback(() => {
    setLoading(true);
    getPlatformOrgs({ limit: rowsPerPage, offset: page * rowsPerPage })
      .then((res) => {
        setOrgs(res?.data?.data || []);
        setTotal(res?.data?.total || 0);
      })
      .catch((err) => {
        console.error('Failed to fetch organizations:', err);
        toast.error('Failed to load organizations');
        setOrgs([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const filteredOrgs = useMemo(() => {
    if (!search.trim()) return orgs;
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => o.organization_name?.toLowerCase().includes(q));
  }, [orgs, search]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Curate which existing Policies and/or Controls ground each of the 6
        narrative-only Knowledge Base cards (Encryption, Logging & Monitoring,
        Incident Response, Business Continuity & DR, Access Control & Identity,
        Personal data practices) for a given organization — these domains have
        no source modal of their own.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="Search organizations"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organization Name</TableCell>
              <TableCell align="right">Sources</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading…' : 'No matching organizations'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredOrgs.map((org) => (
              <TableRow key={org.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {org.organization_name}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" onClick={() => setEditingOrg(org)}>
                    Configure
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {editingOrg && <DomainSourcesDialog org={editingOrg} onClose={() => setEditingOrg(null)} />}
    </Box>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================

const TrinityTrustCenterAccess = () => {
  const [activeTab, setActiveTab] = useState(TAB_KEYS.KNOWLEDGE_BASE);
  const tabIndex = useMemo(() => tabOrder.indexOf(activeTab), [activeTab]);

  const handleTabChange = (_event, newIndex) => {
    setActiveTab(tabOrder[newIndex] || TAB_KEYS.KNOWLEDGE_BASE);
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Trust Center
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Zavior-Admin-only controls for the Knowledge Base "Organization Details"
          gate and every organization's Trust Center sharing setup.
        </Typography>
      </Box>

      <Paper elevation={1}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="Trust Center admin tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabOrder.map((key) => (
            <Tab key={key} label={tabLabels[key]} />
          ))}
        </Tabs>

        <Box padding={3}>
          {activeTab === TAB_KEYS.KNOWLEDGE_BASE && <KnowledgeBaseAccessTab />}
          {activeTab === TAB_KEYS.SHARING_LINKS && <SharingLinksTab />}
          {activeTab === TAB_KEYS.DOMAIN_SOURCES && <DomainSourcesTab />}
        </Box>
      </Paper>
    </Box>
  );
};

export default TrinityTrustCenterAccess;
